import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, UserCheck, UserX, ShieldOff, Building2, TrendingUp, ScrollText,
  KeyRound, Sparkles, Activity, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock } from "@/components/Pagination";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { formatDistanceToNow } from "date-fns";

const ROLE_COLORS = {
  super_admin: "#101876",
  hospital_admin: "#329112",
  faculty: "#0ea5e9",
  department_head: "#f59e0b",
  student: "#8b5cf6",
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  hospital_admin: "Hospital Admin",
  faculty: "Faculty",
  department_head: "Dept. Head",
  student: "Learner",
};

const STATUS_COLORS = {
  active: "#329112",
  inactive: "#94a3b8",
  suspended: "#ef4444",
  pending_verification: "#f59e0b",
};

function ChartCard({ title, subtitle, children, testKey, actions }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={testKey}>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{subtitle}</div>
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
        </div>
        {actions}
      </div>
      <div className="h-64">{children}</div>
    </div>
  );
}

const ACTION_LABELS = {
  login_success: "Logged in",
  login_failed: "Failed login",
  register: "Signed up",
  profile_updated: "Updated profile",
  admin_user_created: "Created user",
  admin_user_updated: "Updated user",
  admin_user_status_changed: "Status change",
  admin_password_reset: "Password reset",
  unauthorized_access: "Blocked access",
};

const ACTION_ICONS = {
  login_success: KeyRound,
  login_failed: AlertTriangle,
  register: Users,
  profile_updated: UserCheck,
  admin_user_created: Users,
  admin_user_updated: UserCheck,
  admin_user_status_changed: ShieldOff,
  admin_password_reset: KeyRound,
  unauthorized_access: AlertTriangle,
};

export default function SuperAdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [loginActivity, setLoginActivity] = useState([]);
  const [orgTypes, setOrgTypes] = useState([]);
  const [statusStats, setStatusStats] = useState([]);
  const [roleStats, setRoleStats] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, m, la, ot, ss, rs, ra] = await Promise.all([
          api.get("/admin/dashboard-summary"),
          api.get("/admin/monthly-user-registration"),
          api.get("/admin/login-activity"),
          api.get("/admin/organization-type-stats"),
          api.get("/admin/account-status-stats"),
          api.get("/admin/user-role-stats"),
          api.get("/admin/recent-activity"),
        ]);
        setSummary(s.data.data);
        setMonthly(m.data.data.buckets);
        setLoginActivity(la.data.data.buckets);
        setOrgTypes(ot.data.data.stats);
        setStatusStats(ss.data.data.stats);
        setRoleStats(rs.data.data.stats);
        setRecent(ra.data.data.logs);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !summary) {
    return (
      <DashboardLayout title="Super Admin Overview" breadcrumbs={["Platform", "Overview"]}>
        <LoadingBlock label="Loading platform overview…" />
      </DashboardLayout>
    );
  }

  const t = summary.totals;

  const roleChartData = roleStats.map((r) => ({
    name: ROLE_LABELS[r.role_code] || r.role_code,
    value: r.count,
    fill: ROLE_COLORS[r.role_code] || "#64748b",
  }));

  return (
    <DashboardLayout title="Super Admin Overview" breadcrumbs={["Platform", "Overview"]}>
      <div className="space-y-8" data-testid={ADMIN.dashboardRoot}>
        {/* KPIs */}
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard to="/app/super-admin/users" label="Total Users" value={t.users} icon="Users" tone="navy" testId={ADMIN.kpi("users")} note={`${t.active_users} active`} index={0} />
          <StatCard to="/app/super-admin/users?status=active" label="Active Users" value={t.active_users} icon="UserCheck" tone="green" testId={ADMIN.kpi("active-users")} index={1} />
          <StatCard to="/app/super-admin/users?status=inactive" label="Inactive Users" value={t.inactive_users} icon="UserX" tone="slate" testId={ADMIN.kpi("inactive-users")} index={2} />
          <StatCard to="/app/super-admin/users?status=suspended" label="Suspended" value={t.suspended_users} icon="ShieldOff" tone="amber" testId={ADMIN.kpi("suspended-users")} index={3} />
        </section>

        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard to="/app/super-admin/organizations" label="Organizations" value={t.organizations} icon="Building2" tone="navy" testId={ADMIN.kpi("orgs")} note={`${t.active_organizations} active`} index={0} />
          <StatCard to="/app/super-admin/audit-logs?action=login_success" label="Login Success" value={t.logins} icon="KeyRound" tone="green" testId={ADMIN.kpi("logins")} note="all-time" index={1} />
          <StatCard to="/app/super-admin/audit-logs?action=login_failed" label="Failed Logins" value={t.failed_logins} icon="AlertTriangle" tone="amber" testId={ADMIN.kpi("failed-logins")} index={2} />
          <StatCard to="/app/super-admin/users" label="New This Month" value={t.new_users_this_month} icon="TrendingUp" tone="green" testId={ADMIN.kpi("new-users-month")} note={`${t.new_orgs_this_month} new orgs`} index={3} />
        </section>

        {/* Role breakdown */}
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {roleStats.map((r, i) => (
            <StatCard
              key={r.role_code}
              to={`/app/super-admin/users?role=${r.role_code}`}
              label={ROLE_LABELS[r.role_code] || r.role_name}
              value={r.count}
              icon={r.role_code === "super_admin" ? "ShieldCheck" : r.role_code === "hospital_admin" ? "Building2" : r.role_code === "faculty" ? "BookOpen" : r.role_code === "department_head" ? "UsersRound" : "GraduationCap"}
              tone={i % 2 === 0 ? "navy" : "green"}
              testId={ADMIN.kpi(`role-${r.role_code}`)}
              index={i}
            />
          ))}
        </section>

        {/* Charts */}
        <section className="grid gap-6 lg:grid-cols-2">
          <ChartCard title="Monthly user registrations" subtitle="6-month trend" testKey={ADMIN.chart("monthly-users")}>
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 12 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="count" name="Users" fill="#101876" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Login activity (last 7 days)" subtitle="Success vs failed" testKey={ADMIN.chart("login-activity")}>
            <ResponsiveContainer>
              <LineChart data={loginActivity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" stroke="#64748b" tick={{ fontSize: 12 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="success" name="Success" stroke="#329112" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="failed" name="Failed" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Users by role" subtitle="Distribution" testKey={ADMIN.chart("role-distribution")}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={roleChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {roleChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Account status" subtitle="Health snapshot" testKey={ADMIN.chart("account-status")}>
            <ResponsiveContainer>
              <BarChart layout="vertical" data={statusStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" stroke="#64748b" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="status" stroke="#64748b" tick={{ fontSize: 12 }} width={110} />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="count" name="Users" radius={[0, 6, 6, 0]}>
                  {statusStats.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.status] || "#64748b"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Organization types" subtitle="Tenant mix" testKey={ADMIN.chart("org-types")}>
            <ResponsiveContainer>
              <BarChart data={orgTypes.filter((o) => o.count > 0)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="type" stroke="#64748b" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis stroke="#64748b" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#329112" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Recent activity list – takes the last chart slot on lg */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={ADMIN.recentActivity}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Live feed</div>
                <h3 className="text-base font-bold text-slate-900">Recent activity</h3>
              </div>
              <Link to="/app/super-admin/audit-logs" className="inline-flex items-center gap-1 text-xs font-semibold text-[#101876] hover:underline">
                <ScrollText className="h-3.5 w-3.5" /> All logs
              </Link>
            </div>
            <ul className="space-y-3">
              {recent.length === 0 && <li className="text-sm text-slate-500">No recent activity.</li>}
              {recent.slice(0, 8).map((r) => {
                const Icon = ACTION_ICONS[r.action] || Activity;
                return (
                  <li key={r.id} className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-[#101876]">
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800 truncate">
                        {ACTION_LABELS[r.action] || r.action}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{r.description || r.user_email}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 whitespace-nowrap">
                      {r.created_at ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true }) : ""}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
